import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";

const statusOptions = ["active", "inactive", "suspended", "trial"];

function calcBMI(heightCm, weightKg) {
  const h = parseFloat(heightCm);
  const w = parseFloat(weightKg);
  if (!h || !w || h <= 0 || w <= 0) return null;
  return w / ((h / 100) * (h / 100));
}

function getBMICategory(bmi) {
  if (bmi === null) return null;
  if (bmi < 18.5) return { label: "Underweight", color: "#3b82f6" };
  if (bmi < 25) return { label: "Normal", color: "#22c55e" };
  if (bmi < 30) return { label: "Overweight", color: "#f59e0b" };
  return { label: "Obese", color: "#ef4444" };
}

function formatWeekLabel(dateStr) {
  const d = new Date(dateStr);
  return `${d?.getMonth() + 1}/${d?.getDate()}`;
}

const statusConfig = {
  active: { color: "var(--color-success)", bg: "var(--color-success)", label: "Active" },
  inactive: { color: "#6b7280", bg: "#6b7280", label: "Inactive" },
  suspended: { color: "var(--color-warning)", bg: "var(--color-warning)", label: "Suspended" },
  trial: { color: "var(--color-primary)", bg: "var(--color-primary)", label: "Trial" },
  archived: { color: "#9ca3af", bg: "#9ca3af", label: "Archived" },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-md p-2 shadow-md text-xs">
      <p className="font-semibold text-[var(--color-foreground)] mb-1">{label}</p>
      {payload?.map((entry) => (
        <p key={entry?.dataKey} style={{ color: entry?.color }}>
          {entry?.name}: {entry?.value != null ? Number(entry?.value)?.toFixed(1) : "—"}
          {entry?.dataKey === "weight" ? " kg" : ""}
        </p>
      ))}
    </div>
  );
};

export default function EditMemberModal({ member, branches, onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    firstName: member?.first_name || "",
    lastName: member?.last_name || "",
    email: member?.email || "",
    phone: member?.phone || "",
    dob: member?.date_of_birth ? String(member.date_of_birth).slice(0, 10) : "",
    branch: member?.branch_id || "",
    status: member?.member_status || "active",
    notes: member?.notes || "",
    heightCm: member?.height ? String(member?.height) : "",
    weightKg: member?.weight ? String(member?.weight) : "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Status action state
  const [statusActionLoading, setStatusActionLoading] = useState(null);
  const [statusLogs, setStatusLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);

  // Trend chart state
  const [chartData, setChartData] = useState([]);
  const [targetWeight, setTargetWeight] = useState(null);
  const [milestoneLines, setMilestoneLines] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);

  const bmiValue = calcBMI(form?.heightCm, form?.weightKg);
  const bmiCategory = getBMICategory(bmiValue);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e?.target?.name]: e?.target?.value }));

  // Fetch status history logs
  const fetchStatusLogs = async () => {
    if (!member?.id) {
      setLogsLoading(false);
      return;
    }
    try {
      setLogsLoading(true);
      // Status logs endpoint not available — show empty
      setStatusLogs([]);
    } catch (err) {
      console.error("Error fetching status logs:", err);
    } finally {
      setLogsLoading(false);
    }
  };

  // Fetch 12-week body measurements + target weight
  useEffect(() => {
    if (!member?.id) {
      setChartLoading(false);
      return;
    }
    const fetchTrendData = async () => {
      setChartLoading(true);
      try {
        const twelveWeeksAgo = new Date();
        twelveWeeksAgo?.setDate(twelveWeeksAgo?.getDate() - 84);

        // Body measurements endpoint not available — use empty data
        const measurementsRes = { data: [] };
        const goalsRes = { data: null };

        const measurements = measurementsRes?.data || [];
        const heightM = member?.height_cm ? member?.height_cm / 100 : null;

        const data = measurements?.map((m) => {
          const w = m?.weight_kg != null ? parseFloat(m?.weight_kg) : null;
          const bmi =
            w && heightM ? parseFloat((w / (heightM * heightM))?.toFixed(1)) : null;
          return {
            date: formatWeekLabel(m?.recorded_at),
            fullDate: m?.recorded_at,
            weight: w != null ? parseFloat(w?.toFixed(1)) : null,
            bmi,
          };
        });

        setChartData(data);

        const tw = goalsRes?.data?.target_weight;
        setTargetWeight(tw ? parseFloat(tw) : null);

        const milestones = [];
        const seen = new Set();
        measurements?.forEach((m) => {
          const d = new Date(m?.recorded_at);
          const key = `${d?.getFullYear()}-${d?.getMonth()}`;
          if (!seen?.has(key)) {
            const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
            const label = `${d?.getMonth() + 1}/1`;
            seen?.add(key);
            milestones?.push({ label, monthName: monthStart?.toLocaleString("default", { month: "short", year: "numeric" }) });
          }
        });
        setMilestoneLines(milestones);
      } catch (err) {
        console.error("Error fetching trend data:", err);
      } finally {
        setChartLoading(false);
      }
    };
    fetchTrendData();
    fetchStatusLogs();
  }, [member?.id, member?.height_cm]);

  // Handle status action (suspend / reactivate / archive)
  const handleStatusAction = async (newStatus) => {
    const previousStatus = form?.status;
    if (previousStatus === newStatus) return;
    try {
      setStatusActionLoading(newStatus);
      setError(null);

      // Update member status
      await api.put(`/members/${member?.id}`, { member_status: newStatus });

      // Update local form state
      setForm((prev) => ({ ...prev, status: newStatus }));

      // Refresh logs
      await fetchStatusLogs();
      onSaved?.();
    } catch (err) {
      console.error("Error updating status:", err);
      setError(err?.message);
    } finally {
      setStatusActionLoading(null);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      setSaving(true);
      setError(null);

      await api.put(`/members/${member?.id}`, {
        first_name: form?.firstName,
        last_name: form?.lastName,
        email: form?.email || null,
        phone: form?.phone || null,
        date_of_birth: form?.dob || null,
        branch_id: form?.branch || null,
        member_status: form?.status,
        notes: form?.notes || null,
        height: form?.heightCm ? parseFloat(form?.heightCm) : null,
        weight: form?.weightKg ? parseFloat(form?.weightKg) : null,
        bmi: bmiValue !== null ? parseFloat(bmiValue?.toFixed(1)) : null,
      });

      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error("Error updating member:", err);
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const currentStatus = form?.status;

  // Determine which action buttons to show based on current status
  const actionButtons = [];
  if (currentStatus === "active" || currentStatus === "trial" || currentStatus === "inactive") {
    actionButtons?.push({ label: "Suspend", newStatus: "suspended", icon: "PauseCircle", colorClass: "border-[var(--color-warning)]/40 text-[var(--color-warning)] hover:bg-[var(--color-warning)]/10" });
    actionButtons?.push({ label: "Archive", newStatus: "archived", icon: "Archive", colorClass: "border-[#9ca3af]/40 text-[#9ca3af] hover:bg-[#9ca3af]/10" });
  } else if (currentStatus === "suspended" || currentStatus === "archived") {
    actionButtons?.push({ label: "Reactivate", newStatus: "active", icon: "PlayCircle", colorClass: "border-[var(--color-success)]/40 text-[var(--color-success)] hover:bg-[var(--color-success)]/10" });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-lg)] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)] sticky top-0 bg-[var(--color-card)] z-10">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-[var(--color-primary)]/15 flex items-center justify-center">
              <Icon name="UserCog" size={14} color="var(--color-primary)" />
            </div>
            <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
              Edit Member
            </h3>
            {/* Current status badge */}
            <span
              className="px-2 py-0.5 rounded-full text-[10px] font-semibold text-white font-[var(--font-caption)] capitalize"
              style={{ backgroundColor: statusConfig?.[currentStatus]?.bg || "#6b7280" }}
            >
              {currentStatus}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors"
          >
            <Icon name="X" size={14} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md">
              <Icon name="AlertCircle" size={14} color="var(--color-error)" />
              <span className="text-xs text-[var(--color-error)] font-[var(--font-caption)]">
                {error}
              </span>
            </div>
          )}

          {/* Status Action Buttons */}
          {actionButtons?.length > 0 && (
            <div className="border border-[var(--color-border)] rounded-md p-3 bg-[var(--color-muted)]/20">
              <p className="text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-2 flex items-center gap-1.5">
                <Icon name="Shield" size={12} color="var(--color-muted-foreground)" />
                Status Actions
              </p>
              <div className="flex flex-wrap gap-2">
                {actionButtons?.map((btn) => (
                  <button
                    key={btn?.newStatus}
                    type="button"
                    disabled={statusActionLoading !== null}
                    onClick={() => handleStatusAction(btn?.newStatus)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-md transition-colors disabled:opacity-60 ${btn?.colorClass}`}
                  >
                    {statusActionLoading === btn?.newStatus ? (
                      <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Icon name={btn?.icon} size={12} color="currentColor" />
                    )}
                    {btn?.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Name */}
          <div className="grid grid-cols-2 gap-3">
            {[
              ["firstName", "First Name"],
              ["lastName", "Last Name"],
            ]?.map(([name, label]) => (
              <div key={name}>
                <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">
                  {label} <span className="text-[var(--color-error)]">*</span>
                </label>
                <input
                  required
                  type="text"
                  name={name}
                  value={form?.[name]}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                />
              </div>
            ))}
          </div>

          {/* Email & Phone */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={form?.email}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={form?.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
            </div>
          </div>

          {/* DOB & Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">
                Date of Birth
              </label>
              <input
                type="date"
                name="dob"
                value={form?.dob}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">
                Status
              </label>
              <select
                name="status"
                value={form?.status}
                onChange={handleChange}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] capitalize"
              >
                {[...statusOptions, "archived"]?.map((s) => (
                  <option key={s} value={s} className="capitalize">
                    {s?.charAt(0)?.toUpperCase() + s?.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Branch */}
          <div>
            <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">
              Branch
            </label>
            <select
              name="branch"
              value={form?.branch}
              onChange={handleChange}
              className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            >
              <option value="">No Branch</option>
              {branches?.map((b) => (
                <option key={b?.id} value={b?.id}>
                  {b?.name}
                </option>
              ))}
            </select>
          </div>

          {/* BMI Section */}
          <div className="border border-[var(--color-border)] rounded-md p-4 bg-[var(--color-muted)]/20 space-y-3">
            <div className="flex items-center gap-2">
              <Icon name="Activity" size={14} color="var(--color-primary)" />
              <h4 className="text-xs font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
                Body Measurements & BMI
              </h4>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">
                  Height (cm)
                </label>
                <input
                  type="number"
                  name="heightCm"
                  value={form?.heightCm}
                  onChange={handleChange}
                  placeholder="e.g. 175"
                  min="50"
                  max="300"
                  step="0.1"
                  className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">
                  Weight (kg)
                </label>
                <input
                  type="number"
                  name="weightKg"
                  value={form?.weightKg}
                  onChange={handleChange}
                  placeholder="e.g. 70"
                  min="10"
                  max="500"
                  step="0.1"
                  className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                />
              </div>
            </div>

            {/* Live BMI Display */}
            {bmiValue !== null ? (
              <div className="flex items-center justify-between p-3 rounded-md border"
                style={{ borderColor: `${bmiCategory?.color}40`, backgroundColor: `${bmiCategory?.color}10` }}>
                <div className="flex items-center gap-2">
                  <Icon name="Scale" size={14} color={bmiCategory?.color} />
                  <span className="text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)]">
                    BMI
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-base font-bold font-[var(--font-data)]" style={{ color: bmiCategory?.color }}>
                    {bmiValue?.toFixed(1)}
                  </span>
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-semibold font-[var(--font-caption)] text-white"
                    style={{ backgroundColor: bmiCategory?.color }}
                  >
                    {bmiCategory?.label}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-3 rounded-md bg-[var(--color-muted)]/40 border border-[var(--color-border)]">
                <Icon name="Info" size={13} color="var(--color-muted-foreground)" />
                <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                  Enter height and weight to calculate BMI
                </span>
              </div>
            )}

            {/* BMI Reference */}
            <div className="grid grid-cols-4 gap-1 text-center">
              {[
                { label: "Underweight", range: "< 18.5", color: "#3b82f6" },
                { label: "Normal", range: "18.5–24.9", color: "#22c55e" },
                { label: "Overweight", range: "25–29.9", color: "#f59e0b" },
                { label: "Obese", range: "≥ 30", color: "#ef4444" },
              ]?.map((cat) => (
                <div
                  key={cat?.label}
                  className="p-1.5 rounded text-xs"
                  style={{
                    backgroundColor: `${cat?.color}15`,
                    border: `1px solid ${cat?.color}30`,
                    opacity: bmiCategory?.label === cat?.label ? 1 : 0.5,
                  }}
                >
                  <div className="font-semibold font-[var(--font-caption)]" style={{ color: cat?.color }}>
                    {cat?.label}
                  </div>
                  <div className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-data)]">
                    {cat?.range}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Weight & BMI Trend Chart */}
          <div className="border border-[var(--color-border)] rounded-md p-4 bg-[var(--color-muted)]/10 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="TrendingUp" size={14} color="var(--color-primary)" />
                <h4 className="text-xs font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
                  12-Week Weight & BMI Trend
                </h4>
              </div>
              {targetWeight && (
                <div className="flex items-center gap-1.5">
                  <div className="w-4 border-t-2 border-dashed border-[#f59e0b]" />
                  <span className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                    Target: {targetWeight} kg
                  </span>
                </div>
              )}
            </div>

            {chartLoading ? (
              <div className="flex items-center justify-center h-40">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs text-[var(--color-muted-foreground)]">Loading trend data…</span>
                </div>
              </div>
            ) : chartData?.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 gap-2">
                <div className="w-10 h-10 rounded-full bg-[var(--color-muted)]/40 flex items-center justify-center">
                  <Icon name="BarChart2" size={18} color="var(--color-muted-foreground)" />
                </div>
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] text-center">
                  No measurement history yet for the last 12 weeks.
                  <br />
                  <span className="text-[10px]">Measurements logged via Member Progress will appear here.</span>
                </p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.5} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
                    tickLine={false}
                    axisLine={{ stroke: "var(--color-border)" }}
                  />
                  {/* Left Y-axis: Weight */}
                  <YAxis
                    yAxisId="weight"
                    orientation="left"
                    tick={{ fontSize: 10, fill: "#6366f1" }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `${v}kg`}
                    width={42}
                  />
                  {/* Right Y-axis: BMI */}
                  <YAxis
                    yAxisId="bmi"
                    orientation="right"
                    tick={{ fontSize: 10, fill: "#22c55e" }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => v}
                    width={30}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{ fontSize: "10px", paddingTop: "4px" }}
                    iconSize={8}
                  />

                  {/* Monthly milestone vertical reference lines */}
                  {milestoneLines?.map((m) => (
                    <ReferenceLine
                      key={m?.label}
                      x={m?.label}
                      yAxisId="weight"
                      stroke="#94a3b8"
                      strokeDasharray="4 2"
                      strokeWidth={1}
                      label={{
                        value: m?.monthName,
                        position: "insideTopLeft",
                        fontSize: 9,
                        fill: "#94a3b8",
                        offset: 4,
                      }}
                    />
                  ))}

                  {/* Target weight dashed horizontal line */}
                  {targetWeight && (
                    <ReferenceLine
                      y={targetWeight}
                      yAxisId="weight"
                      stroke="#f59e0b"
                      strokeDasharray="6 3"
                      strokeWidth={1.5}
                      label={{
                        value: `Target ${targetWeight}kg`,
                        position: "insideTopRight",
                        fontSize: 9,
                        fill: "#f59e0b",
                        offset: 4,
                      }}
                    />
                  )}

                  {/* Weight line */}
                  <Line
                    yAxisId="weight"
                    type="monotone"
                    dataKey="weight"
                    name="Weight (kg)"
                    stroke="#6366f1"
                    strokeWidth={2}
                    dot={{ r: 3, fill: "#6366f1", strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />

                  {/* BMI line */}
                  <Line
                    yAxisId="bmi"
                    type="monotone"
                    dataKey="bmi"
                    name="BMI"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={{ r: 3, fill: "#22c55e", strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={form?.notes}
              onChange={handleChange}
              rows={2}
              placeholder="Optional notes..."
              className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] resize-none"
            />
          </div>

          {/* Status History Log */}
          <div className="border border-[var(--color-border)] rounded-md overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2.5 bg-[var(--color-muted)]/30 border-b border-[var(--color-border)]">
              <Icon name="History" size={13} color="var(--color-primary)" />
              <h4 className="text-xs font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
                Status History Log
              </h4>
              <span className="ml-auto text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Last 10 changes</span>
            </div>
            {logsLoading ? (
              <div className="flex items-center justify-center py-6 gap-2">
                <div className="w-3.5 h-3.5 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-[var(--color-muted-foreground)]">Loading history…</span>
              </div>
            ) : statusLogs?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6 gap-1.5">
                <Icon name="ClipboardList" size={20} color="var(--color-muted-foreground)" />
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No status changes recorded yet</p>
              </div>
            ) : (
              <div className="divide-y divide-[var(--color-border)]">
                {statusLogs?.map((log) => (
                  <div key={log?.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-[var(--color-muted)]/20 transition-colors">
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px] font-semibold text-white capitalize whitespace-nowrap"
                        style={{ backgroundColor: statusConfig?.[log?.previous_status]?.bg || "#6b7280" }}
                      >
                        {log?.previous_status}
                      </span>
                      <Icon name="ArrowRight" size={10} color="var(--color-muted-foreground)" />
                      <span
                        className="px-1.5 py-0.5 rounded text-[10px] font-semibold text-white capitalize whitespace-nowrap"
                        style={{ backgroundColor: statusConfig?.[log?.new_status]?.bg || "#6b7280" }}
                      >
                        {log?.new_status}
                      </span>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                        {log?.changed_by_name || "Unknown"}
                      </p>
                      <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-data)]">
                        {log?.changed_at ? new Date(log?.changed_at)?.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 text-sm font-medium border border-[var(--color-border)] text-[var(--color-foreground)] rounded-md hover:bg-[var(--color-muted)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 text-sm font-semibold bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
